/**
 * /dashboard/website/templates — Template switcher for the logged-in agent.
 * Lists all 20 templates and calls PUT /switch-template for instant change.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ChevronLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardWebsiteTemplatesPage() {
  const { user } = useAuth();
  const agent = user || {};
  const [templates, setTemplates] = useState([]);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [tlist, mine] = await Promise.all([
          axios.get(`${API}/website-templates`),
          agent?.slug
            ? axios.get(`${API}/customer-websites/by-slug/${agent.slug}/full`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        setTemplates(tlist.data.templates || []);
        setSite(mine.data || null);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [agent?.slug]);

  const switchTo = async (template_id) => {
    if (!site?.id) {
      toast.error('No website found — pick an initial template first.');
      return;
    }
    setSwitching(template_id);
    try {
      const r = await axios.put(
        `${API}/customer-websites/${site.id}/switch-template`,
        { template_id }
      );
      // Refresh site
      const refreshed = await axios.get(`${API}/customer-websites/by-slug/${site.slug}/full`);
      setSite(refreshed.data);
      toast.success(`Switched to ${r.data.template_id}. Custom content preserved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Switch failed');
    }
    setSwitching('');
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl" data-testid="switch-templates-page">
      <Link to="/dashboard/website" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
        <ChevronLeft size={14} className="mr-1" /> Back to website
      </Link>
      <header className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Switch template</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Switching keeps your custom content (hero copy, photos, etc.) and just
          changes the layout & style.
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" size={28}/></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {templates.map((t) => {
            const active = site?.template_id === t.template_id;
            return (
              <div key={t.template_id} data-testid={`switch-card-${t.template_id}`}
                   className={`rounded-xl overflow-hidden border bg-background transition ${
                     active ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-border hover:border-muted-foreground/30'}`}>
                <div className="aspect-[4/3]">
                  <img src={`${API}/website-templates/${t.template_id}/preview.svg`}
                       alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.description}</p>
                  </div>
                  <Button
                    data-testid={`switch-use-${t.template_id}`}
                    disabled={!!switching || active}
                    onClick={() => switchTo(t.template_id)}
                    size="sm"
                    className={active ? 'bg-emerald-600 w-full' : 'w-full'}
                    variant={active ? 'default' : 'secondary'}>
                    {switching === t.template_id
                      ? <><Loader2 size={12} className="mr-1 animate-spin"/>Switching…</>
                      : active
                        ? <><CheckCircle2 size={12} className="mr-1"/>Currently in use</>
                        : 'Use this template'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
